import { useEffect, useState } from "react";
import { X, Mail, ExternalLink } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import { tCrm } from "../../lib/crm";

const KEY = "gtx_mailto_banner_dismissed";

/**
 * One-time onboarding banner shown at the top of the CRM that teaches the
 * agent how to set Zoho Mail as the default `mailto:` handler in Chrome.
 * Once dismissed (X) or "Got it" → never appears again on this browser.
 */
export default function MailtoSetupBanner() {
  const { language } = useLanguage();
  const t = (k) => tCrm(language, k);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(KEY);
    if (!dismissed) setOpen(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="mailto-setup-banner"
      className="relative mb-5 rounded-xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 via-orange-500/8 to-amber-500/10 p-4 pr-12"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="mailto-banner-close"
        aria-label="Dismiss"
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/10 text-white/50 hover:text-white"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
          <Mail className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-semibold mb-0.5">
            {t("mailto.tag")}
          </div>
          <div className="text-sm font-bold text-white mb-1.5">{t("mailto.title")}</div>
          <p className="text-xs text-white/70 leading-relaxed mb-2">{t("mailto.subtitle")}</p>
          <ol className="text-xs text-white/70 space-y-1 list-decimal list-inside mb-3">
            <li>{t("mailto.step1")}</li>
            <li>{t("mailto.step2")}</li>
            <li>{t("mailto.step3")}</li>
          </ol>
          <div className="flex flex-wrap items-center gap-2">
            <a
              href="https://mail.zoho.eu"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mailto-banner-open-zoho"
              className="text-xs px-3 py-1.5 rounded-md bg-amber-400/15 text-amber-300 border border-amber-400/30 hover:bg-amber-400/25 inline-flex items-center gap-1.5 font-semibold"
            >
              <ExternalLink className="w-3 h-3" />
              {t("mailto.openZoho")}
            </a>
            <button
              type="button"
              onClick={() => window.open("chrome://settings/handlers", "_blank")}
              data-testid="mailto-banner-handlers"
              className="text-xs px-3 py-1.5 rounded-md bg-white/5 text-white/70 border border-white/15 hover:bg-white/10 hover:text-white"
            >
              {t("mailto.openHandlers")}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="mailto-banner-got-it"
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 font-semibold"
            >
              {t("mailto.gotIt")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
